import { gql } from '@apollo/client'
import { BrandJusIa, Card, Heading } from '@farol-ds/react'
import { Price } from '@jusbrasil-web/shared-price'
import classNames from 'classnames'
import { HTMLAttributes } from 'react'

import { PlanFrequency } from '../../constants'
import { BenefitsList } from '../benefits-list'
import { CheckoutButton } from '../checkout-button'
import { CheckoutButton_OfferLockCardFragment } from '../checkout-button/__generated__/checkout-button.graphql'
import { OneClickButton } from '../one-click-button'
import {
  PlanOfferSimplifiedCard_OfferLockCardFragment,
  PlanOfferSimplifiedCard_ViewerFragment,
} from './__generated__/plan-offer-simplified-card.graphql'
import styles from './plan-offer-simplified-card.module.scss'

export interface PlanOfferSimplifiedCardProps extends HTMLAttributes<HTMLDivElement> {
  viewer: PlanOfferSimplifiedCard_ViewerFragment
  offerLockCard: PlanOfferSimplifiedCard_OfferLockCardFragment
  className?: string
  showCreditCardAndRemoveOneClick?: boolean // this prop will be removed after move one click logic to checkout @ayrton
}

export function PlanOfferSimplifiedCard(props: PlanOfferSimplifiedCardProps) {
  const { offerLockCard, viewer, className, showCreditCardAndRemoveOneClick, ...rest } = props

  const cardClasses = classNames(styles['plan-offer-card'], className)
  const oneClickButtonWrapperClasses = classNames({
    [styles['plan-offer-card-wrapper-button']]: !!offerLockCard.creditCard,
  })
  const isPromotional = !!offerLockCard.offer.priceAfterPromotionalPeriodText
  const isYearlyOffer = offerLockCard?.offer?.frequency === PlanFrequency.YEARLY

  const shouldRenderFrequency =
    offerLockCard.offer.frequency && !offerLockCard.offer.frequencyPeriodText && !isYearlyOffer

  const { offer, title } = offerLockCard
  const { isJusAIOffer } = offer

  return (
    <Card className={cardClasses} {...rest}>
      <Heading className={styles['plan-offer-card-title']} size="lg" asChild>
        <h4>
          {title}
          {isJusAIOffer && (
            <BrandJusIa width={80} height={22} version="positive" data-testid="brand-jus-ia" />
          )}
        </h4>
      </Heading>

      <Price className={styles['plan-offer-card-price']} align="left" size="md">
        {isPromotional ? (
          <Price.Discount>
            de{' '}
            {offer.installmentsText && (
              <strong className={styles['plan-offer-card-price-installments']}>
                {offer.installmentsText}
              </strong>
            )}{' '}
            <Price.DiscountStrike>{offer.priceAfterPromotionalPeriodText}</Price.DiscountStrike>{' '}
            por:
          </Price.Discount>
        ) : (
          <Price.Discount>
            {offer.installmentsText ? (
              <span data-testid="price-label">
                Por apenas{' '}
                <strong className={styles['plan-offer-card-price-installments']}>
                  {offer.installmentsText}
                </strong>{' '}
                de:
              </span>
            ) : (
              <span data-testid="price-label">Por apenas:</span>
            )}
          </Price.Discount>
        )}

        <Price.Value>
          {offer.priceText}
          {shouldRenderFrequency && <Price.ValueFrequency>{offer.frequency}</Price.ValueFrequency>}
        </Price.Value>
        {offer.frequencyPeriodText && (
          <Price.Helptext>no {offer.frequencyPeriodText}</Price.Helptext>
        )}
      </Price>
      {showCreditCardAndRemoveOneClick ? (
        <>
          <CheckoutButton offerLockCard={offerLockCard} />
          <div className={oneClickButtonWrapperClasses}>
            <CheckoutButton
              offerLockCard={{
                ...offerLockCard,
                creditCard: {
                  ...offerLockCard.creditCard,
                  ccToken: null,
                } as CheckoutButton_OfferLockCardFragment['creditCard'],
              }}
            />
          </div>
        </>
      ) : (
        <>
          <CheckoutButton offerLockCard={offerLockCard} />
          <div className={oneClickButtonWrapperClasses}>
            <OneClickButton offerLockCard={offerLockCard} viewer={viewer} />
          </div>
        </>
      )}

      <BenefitsList
        offerLockCard={offerLockCard}
        benefitsListClassName={styles['plan-offer-card-benefits']}
      />
    </Card>
  )
}

PlanOfferSimplifiedCard.fragments = {
  offerLockCard: gql`
    fragment PlanOfferSimplifiedCard_offerLockCard on OfferLockCard {
      title
      subtitle
      offer {
        frequencyPeriodText
        installmentsText
        priceText
        priceAfterPromotionalPeriodText
        frequency
        isJusAIOffer
      }
      ...OneClickButton_offerLockCard
      ...CheckoutButton_offerLockCard
      ...BenefitsList_offerLockCard
    }
    ${OneClickButton.fragments.offerLockCard}
    ${CheckoutButton.fragments.offerLockCard}
    ${BenefitsList.fragments.offerLockCard}
  `,
  offerLockBody: gql`
    fragment PlanOfferSimplifiedCard_offerLockBody on OfferLockBody {
      cards {
        offer {
          priceAfterPromotionalPeriodText
        }
      }
    }
  `,
  viewer: gql`
    fragment PlanOfferSimplifiedCard_viewer on Profile {
      ...OneClickButton_viewer
    }
    ${OneClickButton.fragments.viewer}
  `,
}
